import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import illustration from 'assets/img/auth/auth.png';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordReset, EMAIL_REGEX } from 'services/firebase';
import { useCheckIfUserExistsByEmail } from 'domains/auth/hooks/useCheckIfUserExistsByEmail';
import DefaultAuth from 'layouts/auth/Default';
import { useTranslation } from 'i18n/TranslationProvider';
import { UnifiedAuthPanel } from 'components/layout/UnifiedPrimitives';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { checkIfUserExistsByEmail } = useCheckIfUserExistsByEmail();
  const mutedText = useColorModeValue('gray.600', 'gray.300');

  const handleSendReset = async () => {
    const trimmedEmail = email.trim();

    // Basic validation
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
      toast({
        title: t('auth.invalid_email'),
        description: t('auth.please_enter_a_valid_email_address'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // ✅ Check if email exists first
      const data = await checkIfUserExistsByEmail(trimmedEmail);
      console.log(data);
      if (!data?.exists) {
        toast({
          title: t('auth.email_not_found'),
          description: t('auth.no_account_found_with_that_email_address'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return; // stop here
      }

      // ✅ If email exists, send password reset
      const message = await sendPasswordReset(trimmedEmail);
      toast({
        title: t('auth.email_sent'),
        description: message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setShowConfirmation(true);
      setEmail(''); // clear input
    } catch (error) {
      toast({
        title: t('auth.error'),
        description: error.message || t('auth.something_went_wrong_try_again'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration}>
      <Box w="100%" maxW={{ base: '100%', sm: '540px', md: '620px' }} mx="auto">
        <UnifiedAuthPanel>
          {showConfirmation ? (
            <Stack spacing={5}>
              <Heading size={{ base: 'lg', md: 'xl' }} fontWeight="800">
                {t('auth.check_your_email')}
              </Heading>
              <Text color={mutedText}>
                {t('auth.we_sent_a_password_reset_link_open_your_inbox_and_follow_the_instructions')}
              </Text>
              <Button
                variant="brand"
                onClick={() => navigate('/auth/sign-in')}
                h={{ base: '46px', md: '50px' }}
                borderRadius="14px"
              >
                {t('auth.back_to_sign_in')}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={5}>
              <Heading size={{ base: 'lg', md: 'xl' }} fontWeight="800">
                {t('auth.forgot_your_password')}
              </Heading>
              <Text color={mutedText}>
                {t('auth.enter_your_account_email_and_we_will_send_you_a_password_reset_link')}
              </Text>
              <Box>
                <Text fontWeight="600" fontSize="sm" mb={2}>
                  {t('auth.email_address')}
                </Text>
                <Input
                  type="email"
                  placeholder={t('auth.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  h={{ base: '46px', md: '50px' }}
                />
              </Box>
              <HStack spacing={3} flexWrap="wrap">
                <Button
                  variant="brand"
                  onClick={handleSendReset}
                  isLoading={loading}
                  h={{ base: '46px', md: '50px' }}
                  px={8}
                  borderRadius="14px"
                  w={{ base: '100%', sm: 'auto' }}
                >
                  {t('auth.send_reset_link')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/auth/sign-in')}
                  h={{ base: '46px', md: '50px' }}
                  px={8}
                  borderRadius="14px"
                  w={{ base: '100%', sm: 'auto' }}
                >
                  {t('auth.cancel')}
                </Button>
              </HStack>
            </Stack>
          )}
        </UnifiedAuthPanel>
        {!showConfirmation ? (
          <Box mt={4}>
            <Text color={mutedText} fontSize="sm">
              {t('auth.tip_if_you_do_not_receive_the_email_check_spam_and_promotions_folders')}
            </Text>
          </Box>
        ) : null}
      </Box>
    </DefaultAuth>
  );
}
